import {
  AUCTION_REQUEST_ACCEPTED,
  AUCTION_REQUEST_ATTRIBUTED,
  AUCTION_REQUEST_CREATED,
  AUCTION_REQUEST_SENT,
  QUERY_PROGRESS_NOT_STARTED,
  REQUEST_ACTION_VALIDATE_PROVIDER,
} from "core/consts";
import { findRequestAction } from "core/model/requests";
import { useGetTranslationForSearchType } from "core/model/utils/ontologies/hooks";
import { renderMultiLine } from "core/model/utils/text";
import { AuctionRequest } from "core/types";
import { useTranslations } from "translations";
import {
  DEFAULT_ICON,
  EventCardMessage,
  EventCardProps,
  PENDING_ICON,
  SUCCESS_ICON,
} from "../EventCard";
import { StatusEventProps } from "../EventMessaging";

export function useStatusTranslation(auctionRequest: AuctionRequest) {
  const translations = useTranslations();
  const validateRequestAction = findRequestAction(
    auctionRequest,
    REQUEST_ACTION_VALIDATE_PROVIDER,
  );
  const getTranslationForSearchType = useGetTranslationForSearchType();

  switch (auctionRequest.status) {
    case AUCTION_REQUEST_ATTRIBUTED:
      return getTranslationForSearchType({
        receiverType: "receiverTypeSingular",
        searchType: auctionRequest?.auction?.search_type,
        translationKey: "search.foundProviderTitle",
      });
    case AUCTION_REQUEST_ACCEPTED: {
      return validateRequestAction
        ? translations.requestEvents.requestAccepted
        : translations.responseEvents.waitingForClinic;
    }
    case AUCTION_REQUEST_CREATED:
      return translations.faxRequest.faxEvents.faxEventTitle;
    case AUCTION_REQUEST_SENT:
      return translations.requestEvents.waitingForProviderAnswer;
    default:
      return null;
  }
}

export function useGetStatusEventPropsCareseeker(
  auctionRequest: AuctionRequest,
): StatusEventProps {
  const statusTranslation = useStatusTranslation(auctionRequest);
  const validateRequestAction = findRequestAction(
    auctionRequest,
    REQUEST_ACTION_VALIDATE_PROVIDER,
  );

  switch (auctionRequest.status) {
    case AUCTION_REQUEST_ATTRIBUTED:
      return {
        cardFormat: { variant: "success" },
        Icon: SUCCESS_ICON,
        statusHeader: statusTranslation,
        testId: "request_attributed",
      };
    case AUCTION_REQUEST_ACCEPTED: {
      return {
        cardFormat: {},
        Icon: DEFAULT_ICON,
        statusHeader: validateRequestAction
          ? renderMultiLine(statusTranslation) || ""
          : statusTranslation,
        testId: "request_accepted",
      };
    }
    case AUCTION_REQUEST_CREATED:
      return {
        cardFormat: {},
        Icon: DEFAULT_ICON,
        statusHeader: statusTranslation,
        testId: "onboarding",
      };
    case AUCTION_REQUEST_SENT:
      return {
        cardFormat: {},
        Icon: PENDING_ICON,
        statusHeader: statusTranslation,
        testId: "waiting_for_provider_answer",
      };
    default:
      return null;
  }
}

// todo: see if needed only used in tests
export function EventTitleCareseeker({
  auctionRequest,
  isDividerVisible,
  shouldShowBefore,
}: {
  auctionRequest: AuctionRequest;
  isDividerVisible: EventCardProps["isDividerVisible"];
  shouldShowBefore: EventCardProps["shouldShowBefore"];
}) {
  const statusEventProps = useGetStatusEventPropsCareseeker(auctionRequest);

  if (!statusEventProps) return null;

  return (
    <EventCardMessage
      cancelMutationProgress={QUERY_PROGRESS_NOT_STARTED}
      cardFormat={statusEventProps.cardFormat}
      handleCancel={null}
      Icon={statusEventProps.Icon}
      isDividerVisible={isDividerVisible}
      messages={null}
      shouldShowBefore={shouldShowBefore}
      statusHeader={statusEventProps.statusHeader}
      testId={statusEventProps.testId}
    />
  );
}

export default EventTitleCareseeker;
